
import {inject} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {isAnyAdmin, isUserProfileLoaded, selectTenantInfo, selectUser} from '../store/selectors';
import { filter, map, startWith} from 'rxjs/operators';
import {combineLatest, timer} from 'rxjs';
import {Auth, authState} from '@angular/fire/auth';
import {isUserAllowedInCommunity} from './utils';

/**
 * How long the guard will wait for the Firestore user profile before deciding without it.
 *
 * Long enough to cover a cold load on a slow connection, short enough that a profile which
 * never arrives (a failed `findUserByUid`, say) does not leave the member staring at a
 * spinner - they get the pre-existing behaviour instead, which is a redirect they can
 * recover from.
 */
const PROFILE_WAIT_MS = 8000;
import {getFullRouterPath} from '../common/router-utils';
import {MessagesService} from '../shared/services/messages.service';
import {CommunitySettings} from '../models/community/community-settings.model';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';
export const communityAccessGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {

    const messages = inject(MessagesService);
    const store = inject(Store);
    const router = inject(Router);
    const auth = inject(Auth);
    const userPermissionsService = inject(UserPermissionsService);
    const permissionKeys = PermissionKeys;

    console.log(`COMMUNITY GUARD Calling community access guard...`);


    // wait for the user profile to be loaded before deciding to give access to the community

    // Escape hatch for the wait below, so a profile that never arrives degrades to the old
    // behaviour (decide with what we have) instead of hanging the route forever on a
    // spinner. `startWith(false)` is what lets combineLatest emit before the timer fires.
    const giveUpWaiting$ = timer(PROFILE_WAIT_MS).pipe(map(() => true), startWith(false));

    return combineLatest([
      store.pipe(select(selectUser)),
      store.pipe(select(selectTenantInfo)),
      store.pipe(select(isAnyAdmin)),
      store.pipe(select(isUserProfileLoaded)),
      authState(auth),
      giveUpWaiting$
    ])
      .pipe(
        filter(([user, tenant, isAnyAdmin, isUserProfileLoaded, authUser, giveUpWaiting]) => {
          if (!user || !tenant) {
            return false;
          }

          // WAIT FOR THE FIRESTORE PROFILE BEFORE DECIDING. This is the fix for
          // "refresh on a room / Events / Saved throws me out to the products page".
          //
          // Two different actions populate the user, in this order:
          //   1. `login`      - from the Firebase auth state. Carries id/email/displayName
          //                     and NOTHING else. Does NOT set `userProfileLoaded`.
          //   2. `userLoaded` - the Firestore document, which is where
          //                     `communityMemberSettings` lives. Sets `userProfileLoaded`.
          //
          // `isUserAllowedInCommunity` reads `communityMemberSettings` to decide whether a
          // member has bought or been invited to the community. Between (1) and (2) that
          // field is simply absent, so a paying member reads as "not a member" - the guard
          // fell through to its unknown-scenario branch, showed "You do not have access to
          // this page." and redirected to /courses.
          //
          // It only bit on a hard refresh, because in-app navigation happens long after
          // (2). And it only bit OUTSIDE /community/home, because the paid/invite-only
          // branch further down lets home through unconditionally - which is exactly the
          // shape QA reported: home survives a refresh, a room does not, and so do Events
          // and Saved.
          //
          // THE WAIT IS KEYED ON `authState`, NOT ON THE STORE. This is the part that is
          // easy to get wrong, and getting it wrong is why the original check was
          // commented out rather than fixed.
          //
          // The store cannot answer "is anybody signed in?" during a cold load. Its
          // initial user is ANONYMOUS_USER - a real, TRUTHY object - and `isLoggedIn` is
          // false, and BOTH stay that way until Firebase has restored the session and the
          // `login$` effect fires. Nothing dispatches `logout` on a null auth state at
          // bootstrap (`login$` does `filter(user => !!user)`), so a signed-out visitor
          // and a signed-in-but-not-yet-restored member look identical in the store. A
          // guard keyed on either one therefore decides for a paying member while holding
          // ANONYMOUS_USER, denies, and redirects - which is the bug.
          //
          // `authState(auth)` does not emit until Firebase has resolved, and combineLatest
          // will not produce a value until every source has emitted, so simply having it
          // in the list closes that window. Its value then says which case we are in:
          //   - a User  -> a profile IS coming; wait for `userProfileLoaded` as well.
          //   - null    -> nobody is signed in, no profile is coming, and waiting for one
          //                would hang every guest forever. Decide now and let
          //                `isUserAllowedInCommunity` deny properly.
          const profileIsStillComing = !!authUser && !isUserProfileLoaded && !giveUpWaiting;

          if (profileIsStillComing) {
            console.log(`COMMUNITY GUARD User profile not loaded yet, waiting before deciding access.`);
            return false;
          }

          // all other cases, we are ready to decide if the user has access or not
          return true;
        }),
        map(([user, tenantInfo, isAnyAdmin]) => {

          const currentUser = user;
          const isAdmin = isAnyAdmin;
          const communitySettings: CommunitySettings = tenantInfo?.communitySettings;

          console.log(`COMMUNITY GUARD checking access, currentUser: `, currentUser);
          console.log(`COMMUNITY GUARD checking access, isAdmin: `, isAdmin);
          console.log(`COMMUNITY GUARD checking access, communitySettings: `, communitySettings);


          

          if (communitySettings?.disabled && isAdmin) {// if the community is disabled and the user is an admin, redirect to the community settings
            console.log(`COMMUNITY GUARD Community is disabled, redirecting to community settings...`);
            return router.parseUrl('/community/settings');
          }

          if (communitySettings?.disabled && !isAdmin && !userPermissionsService.can(permissionKeys.CommunitySettings)) { // if the community is disabled and the user is not an admin, redirect to the courses screen
            console.log(`COMMUNITY GUARD Community is disabled, redirecting to courses screen...`);
            return router.parseUrl('/courses');
          }

          // if user is banned, deny access to the community and redirect to courses page
          if (currentUser?.communityMemberSettings?.banned) {
            messages.error('You do not have access to this community.');
            console.log(`COMMUNITY GUARD User is banned, redirecting to courses page...`);
            return router.parseUrl('/courses');
          }

          const url = getFullRouterPath(route);
  

          // ✅ Allow pages if customRole user has  permission
          // start
          if ((url?.includes('manage-rooms') || url?.includes('rooms'))  && userPermissionsService.can(permissionKeys.CommunityManageRooms)) {
            console.log(`COMMUNITY GUARD User has 'manageRoomAllowed' permission → allowing manage-room access`);
            return true;
          }

          if (url?.includes('members') && userPermissionsService.can(permissionKeys.CommunityMembers)) {
            console.log(`COMMUNITY GUARD User has 'communityMembersAllowed' permission → allowing members access`);
            return true;
          }

          if (url?.includes('my-profile') && userPermissionsService.can(permissionKeys.Community)) {
            console.log(`COMMUNITY GUARD User has 'communityMyProfileAllowed' permission → allowing my-profile access`);
            return true;
          }
          
          // end

          // first check if the community is active
          const isNavigatingToCommunitySettings = url?.includes('community') && url?.endsWith('settings');
          console.log(`COMMUNITY GUARD Is navigating to community settings: ${isNavigatingToCommunitySettings}`, url);

          // if there are no community settings yet, redirect to the settings screen so that the community can be initialized
          if (!communitySettings && !isNavigatingToCommunitySettings) {
            console.log(`COMMUNITY GUARD Community not initialized yet, redirecting to settings screen.`);
            return router.parseUrl('/community/settings');
          }

          // then check if it's a paid or invite-only community.
          // In that case, the user should always be allowed to the home screen
          const isNavigatingToCommunityHome = url?.endsWith('home');

          console.log(`COMMUNITY GUARD Is navigating to community home: ${isNavigatingToCommunityHome}`, url);

          if (isNavigatingToCommunityHome && (
            communitySettings?.communityAccess == 'community-subscription' ||
            communitySettings?.communityAccess == 'invite-only')) {
            console.log(`COMMUNITY GUARD Community is paid or invite-only, allowing access to home screen.`);
            return true;
          }

          // if it's not any of the previous special cases, then check if the user is allowed
          const isAllowed = isUserAllowedInCommunity(communitySettings, isAdmin, currentUser);

          if (isAllowed) {
            console.log(`COMMUNITY GUARD User is allowed in this route.`);
            return true;
          }
          else {
            console.log(`COMMUNITY GUARD User is NOT allowed in this route...`);
          }

          // if the user is not allowed, not sure what this scenario is, as we should have covered all cases before
          // send the user outside the community and give a generic error
          messages.error('You do not have access to this page.');
          console.log(`COMMUNITY GUARD access denied, unknown scenario, redirecting to courses screen...`);
          return router.parseUrl('/courses');
        })
      );
  };

